import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { blogadd, blogCategories, blogUploadImage } from './proxyurls';
import Select from 'react-select';
import Layout from './layout';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import 'react-datepicker/dist/react-datepicker.css';
import DatePicker from 'react-datepicker';

const statusOptions = [
    { value: 1, label: 'In-Active' },
    { value: 0, label: 'Active' }
];

function AddBlog() {
    const [formData, setFormData] = useState({
        name: '',
        slug: '',
        metakeywords: '',
        metatitle: '',
        meta_description: '',
        canonical: '',
        alttext: '',
        status: 0,
        category: '',
        image: null,
        content: '',
        publish_datetime: new Date().toISOString().split('T')[0]
    });
    const [formErrors, setFormErrors] = useState({});
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [imagePreview, setImagePreview] = useState(null);
    const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
    const [categories, setCategories] = useState([]);
    const [categoryOptions, setCategoryOptions] = useState([]);
    const navigate = useNavigate();
    const editorRef = useRef(null);

    // Function to generate slug from title
    const generateSlug = (title) => {
        return title
            .toLowerCase()
            .trim()
            .replace(/[^\w\s-]/g, '') // Remove special characters
            .replace(/[\s_-]+/g, '-') // Replace spaces and underscores with hyphens
            .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
    };

    // Fetch categories on component mount
    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const response = await axios.get(blogCategories);
                const categoriesData = Array.isArray(response.data) ? response.data : (response.data.categories || response.data.data || []);
                setCategories(categoriesData);
                
                // Convert to react-select format
                const options = categoriesData.map(category => ({
                    value: category.id || category._id || category.name,
                    label: category.name || category.title || category.label
                }));
                setCategoryOptions(options);
            } catch (error) {
                console.error('Error fetching categories:', error);
                setErrorMessage('Error fetching categories');
                setTimeout(() => setErrorMessage(''), 3000);
            }
        };

        fetchCategories();
    }, []);

    const handleChange = (e) => {
        const { name, value, files } = e.target;
        if (name === 'image' && files && files[0]) {
            const file = files[0];
            setFormData(prevData => ({
                ...prevData,
                [name]: file
            }));
            // Create preview
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        } else if (name === 'name') {
            // When title changes, auto-generate slug if it hasn't been manually edited
            setFormData(prevData => ({
                ...prevData,
                [name]: value,
                slug: slugManuallyEdited ? prevData.slug : generateSlug(value)
            }));
        } else if (name === 'slug') {
            // Track if slug is manually edited
            setSlugManuallyEdited(true);
            setFormData(prevData => ({
                ...prevData,
                [name]: value
            }));
        } else {
            setFormData(prevData => ({
                ...prevData,
                [name]: value
            }));
        }
    };

    const handleSelectChange = (selectedOption, fieldName) => {
        setFormData(prevData => ({
            ...prevData,
            [fieldName]: selectedOption ? selectedOption.value : ''
        }));
    };

    const handleDateChange = (date, fieldName) => {
        if (date) {
            // Format as date string (YYYY-MM-DD)
            const dateString = date.toISOString().split('T')[0];
            setFormData(prevData => ({
                ...prevData,
                [fieldName]: dateString
            }));
        } else {
            setFormData(prevData => ({
                ...prevData,
                [fieldName]: ''
            }));
        }
    };

    const handleContentChange = (event, editor) => {
        const data = editor.getData();
        setFormData(prevData => ({
            ...prevData,
            content: data
        }));
    };

    // CKEditor upload adapter for images
    const uploadAdapterPlugin = (editor) => {
        try {
            const fileRepository = editor.plugins.get('FileRepository');
            if (!fileRepository) {
                console.error('FileRepository plugin not found');
                return;
            }

            fileRepository.createUploadAdapter = (loader) => {
                return {
                    upload: () => {
                        if (!loader || !loader.file) {
                            return Promise.reject(new Error('File loader is not available'));
                        }

                        return loader.file.then(file => {
                            if (!file) {
                                return Promise.reject(new Error('No file selected'));
                            }

                            // Validate file type
                            if (!file.type || !file.type.match(/^image\/(jpeg|jpg|png|gif|webp)$/)) {
                                const errorMsg = 'Invalid image format. Please use JPEG, PNG, GIF, or WebP';
                                setErrorMessage(errorMsg);
                                setTimeout(() => setErrorMessage(''), 5000);
                                return Promise.reject(new Error(errorMsg));
                            }

                            // Upload image to backend
                            const imageFormData = new FormData();
                            imageFormData.append('image', file);

                            return axios.post(blogUploadImage, imageFormData, {
                                withCredentials: true
                            }).then(response => {
                                // Get imageUrl from response (handle different response formats)
                                const imageUrl = response.data?.imageUrl || 
                                              response.data?.url || 
                                              response.data?.image || 
                                              response.data?.data?.imageUrl ||
                                              response.data?.data?.url ||
                                              '';
                                
                                if (!imageUrl) {
                                    const errorMsg = 'Failed to get image URL from upload response';
                                    setErrorMessage(errorMsg);
                                    setTimeout(() => setErrorMessage(''), 5000);
                                    return Promise.reject(new Error(errorMsg));
                                }

                                setSuccessMessage('Image uploaded successfully');
                                setTimeout(() => setSuccessMessage(''), 3000);

                                // Return the image URL to CKEditor
                                return {
                                    default: imageUrl
                                };
                            }).catch(error => {
                                const errorMsg = error.response?.data?.error || error.response?.data?.message || 'Error uploading image';
                                setErrorMessage(errorMsg);
                                setTimeout(() => setErrorMessage(''), 5000);
                                return Promise.reject(new Error(errorMsg));
                            });
                        }).catch(error => {
                            const errorMsg = error.message || 'Error loading file';
                            setErrorMessage(errorMsg);
                            setTimeout(() => setErrorMessage(''), 5000);
                            return Promise.reject(new Error(errorMsg));
                        });
                    },
                    abort: () => {
                        // Handle upload cancellation if needed
                    }
                };
            };
        } catch (error) {
            console.error('Error setting up upload adapter:', error);
            setErrorMessage('Image upload feature not available.');
            setTimeout(() => setErrorMessage(''), 5000);
        }
    };

    // CKEditor configuration
    const editorConfiguration = {
        toolbar: [
            'heading',
            '|',
            'bold',
            'italic',
            'underline',
            'strikethrough',
            '|',
            'bulletedList',
            'numberedList',
            '|',
            'outdent',
            'indent',
            '|',
            'link',
            'image',
            '|',
            'blockQuote',
            'insertTable',
            '|',
            'undo',
            'redo'
        ],
        heading: {
            options: [
                { model: 'paragraph', title: 'Paragraph', class: 'ck-heading_paragraph' },
                { model: 'heading1', view: 'h1', title: 'Heading 1', class: 'ck-heading_heading1' },
                { model: 'heading2', view: 'h2', title: 'Heading 2', class: 'ck-heading_heading2' },
                { model: 'heading3', view: 'h3', title: 'Heading 3', class: 'ck-heading_heading3' },
                { model: 'heading4', view: 'h4', title: 'Heading 4', class: 'ck-heading_heading4' },
                { model: 'heading5', view: 'h5', title: 'Heading 5', class: 'ck-heading_heading5' },
                { model: 'heading6', view: 'h6', title: 'Heading 6', class: 'ck-heading_heading6' }
            ]
        },
        image: {
            toolbar: [
                'imageTextAlternative',
                'toggleImageCaption',
                'imageStyle:inline',
                'imageStyle:block',
                'imageStyle:side'
            ],
            upload: {
                types: ['jpeg', 'jpg', 'png', 'gif', 'webp']
            },
            insert: {
                integrations: ['upload', 'url']
            },
            resizeUnit: 'px',
            styles: ['full', 'side', 'alignLeft', 'alignCenter', 'alignRight']
        }
    };

    const validateForm = () => {
        const errors = {};
        if (!formData.name) errors.name = 'Title is required';
        if (!formData.slug) errors.slug = 'Slug is required';
        if (!formData.content) errors.content = 'Content is required';
        return errors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const validationErrors = validateForm();
        if (Object.keys(validationErrors).length > 0) {
            setFormErrors(validationErrors);
            return;
        }
        try {
            let imageUrl = '';
            
            // If image is present, upload it first
            if (formData.image) {
                const imageFormData = new FormData();
                imageFormData.append('image', formData.image);
                
                const uploadResponse = await axios.post(blogUploadImage, imageFormData, {
                    withCredentials: true
                });
                
                // Get imageUrl from response (handle different response formats)
                imageUrl = uploadResponse.data?.imageUrl || 
                          uploadResponse.data?.url || 
                          uploadResponse.data?.image || 
                          uploadResponse.data?.data?.imageUrl ||
                          uploadResponse.data?.data?.url ||
                          '';
                
                if (!imageUrl) {
                    setErrorMessage('Failed to get image URL from upload response');
                    return;
                }
            }

            // Now submit the blog data with imageUrl
            const submitData = new FormData();
            submitData.append('name', formData.name);
            submitData.append('slug', formData.slug);
            submitData.append('meta_keywords', formData.metakeywords);
            submitData.append('meta_title', formData.metatitle);
            submitData.append('meta_description', formData.meta_description);
            submitData.append('cannonical_link', formData.canonical);
            submitData.append('alttext', formData.alttext);
            submitData.append('status', formData.status);
            submitData.append('category', formData.category);
            submitData.append('content', formData.content);
            submitData.append('imagetype', 3);
            submitData.append('type', 'vls');
            submitData.append('publish_datetime', formData.publish_datetime || '');
            if (imageUrl) {
                submitData.append('featured_image', imageUrl);
            }

            await axios.post(blogadd, submitData, {
                withCredentials: true
            });
            setSuccessMessage('Blog added successfully');
            setErrorMessage('');
            localStorage.setItem('successMessage', 'Blog added successfully');
            setTimeout(() => {
                navigate('/manage-blogs');
                setSuccessMessage('');
            }, 1000);
        } catch (error) {
            console.error('Error adding blog:', error);
            setErrorMessage(error.response?.data?.error || error.response?.data?.message || 'An error occurred');
        }
    };

    const handleCancel = () => {
        navigate('/manage-blogs');
    };

    return (
        <Layout>
            <div style={{ padding: '8px' }}>
                {successMessage && <div className="alert alert-success">{successMessage}</div>}
                {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}
                <h3 className='py-3 px-3 border-bottom'>Add Blog</h3>
                <form onSubmit={handleSubmit} className='py-3 px-3'>
                    <div className="row mb-3">
                        <div className="col-6">
                            <label htmlFor="name" className="form-label">Title</label>
                            <input
                                type="text"
                                id="name"
                                className="form-control"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                            />
                            {formErrors.name && <div className="text-danger">{formErrors.name}</div>}
                        </div>
                        <div className="col-6">
                            <label htmlFor="slug" className="form-label">Slug</label>
                            <input
                                type="text"
                                id="slug"
                                className="form-control"
                                name="slug"
                                value={formData.slug}
                                onChange={handleChange}
                            />
                            {formErrors.slug && <div className="text-danger">{formErrors.slug}</div>}
                        </div>
                    </div>
                    <div className="row mb-3">
                        <div className="col-6">
                            <label htmlFor="metatitle" className="form-label">Meta Title</label>
                            <input
                                type="text"
                                id="metatitle"
                                className="form-control"
                                name="metatitle"
                                value={formData.metatitle}
                                onChange={handleChange}
                            />
                        </div>
                        <div className="col-6">
                            <label htmlFor="metakeywords" className="form-label">Meta Keywords</label>
                            <input
                                type="text"
                                id="metakeywords"
                                className="form-control"
                                name="metakeywords"
                                value={formData.metakeywords}
                                onChange={handleChange}
                                placeholder="comma separated keywords"
                            />
                        </div>
                    </div>
                    <div className="row mb-3">
                        <div className="col-12">
                            <label htmlFor="meta_description" className="form-label">Meta Description</label>
                            <textarea
                                id="meta_description"
                                className="form-control"
                                name="meta_description"
                                rows="3"
                                value={formData.meta_description}
                                onChange={handleChange}
                            />
                        </div>
                    </div>
                    <div className="row mb-3">
                        <div className="col-6">
                            <label htmlFor="canonical" className="form-label">Canonical</label>
                            <input
                                type="text"
                                id="canonical"
                                className="form-control"
                                name="canonical"
                                value={formData.canonical}
                                onChange={handleChange}
                                placeholder="https://example.com/blog-post"
                            />
                        </div>
                        <div className="col-6">
                            <label htmlFor="category" className="form-label">Category</label>
                            <Select
                                id="category"
                                name="category"
                                options={categoryOptions}
                                value={categoryOptions.find(option => option.value === formData.category || option.value === String(formData.category))}
                                onChange={option => handleSelectChange(option, 'category')}
                                placeholder="Select a category"
                                isClearable
                            />
                        </div>
                    </div>
                    <div className="row mb-3">
                        <div className="col-6">
                            <label htmlFor="status" className="form-label">Status</label>
                            <Select
                                id="status"
                                name="status"
                                options={statusOptions}
                                value={statusOptions.find(option => option.value === formData.status)}
                                onChange={option => handleSelectChange(option, 'status')}
                            />
                        </div>
                        <div className="col-6">
                            <label htmlFor="image" className="form-label">Image</label>
                            <input
                                type="file"
                                id="image"
                                className="form-control"
                                name="image"
                                accept="image/*"
                                onChange={handleChange}
                            />
                            {imagePreview && (
                                <div className="mt-2">
                                    <img src={imagePreview} alt="Preview" style={{ maxWidth: '200px', maxHeight: '200px' }} />
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="row mb-3">
                        <div className="col-6">
                            <label htmlFor="alttext" className="form-label">Alt Text</label>
                            <input
                                type="text"
                                id="alttext"
                                className="form-control"
                                name="alttext"
                                value={formData.alttext}
                                onChange={handleChange}
                            />
                        </div>
                        <div className="col-6">
                            <label htmlFor="publish_datetime" className="form-label">Publish Date & Time</label>
                            <DatePicker
                                id="publish_datetime"
                                className="form-control"
                                selected={formData.publish_datetime ? new Date(formData.publish_datetime) : new Date()}
                                onChange={date => handleDateChange(date, 'publish_datetime')}
                                dateFormat="yyyy-MM-dd"
                            />
                        </div>
                    </div>
                    <div className="row mb-3">
                        <div className="col-12">
                            <label htmlFor="content" className="form-label">Content</label>
                            <style>{`
                                .ck-editor__editable {
                                    min-height: 300px;
                                }
                                .ck.ck-editor__main>.ck-editor__editable:not(.ck-focused) {
                                    height: 300px;
                                }
                                .ck-content {
                                    height: 300px;
                                }
                            `}</style>
                            <CKEditor
                                editor={ClassicEditor}
                                config={editorConfiguration}
                                data={formData.content}
                                onReady={(editor) => {
                                    editorRef.current = editor;
                                    
                                    // Debug: Check if image plugin is available
                                    try {
                                        const imagePlugin = editor.plugins.get('Image');
                                        console.log('Image plugin found:', imagePlugin);
                                        
                                        // Check available toolbar items
                                        const toolbarItems = editor.config.get('toolbar');
                                        console.log('Toolbar items:', toolbarItems);
                                    } catch (e) {
                                        console.error('Image plugin not found:', e);
                                    }
                                    
                                    uploadAdapterPlugin(editor);
                                }}
                                onChange={handleContentChange}
                            />
                            {formErrors.content && <div className="text-danger">{formErrors.content}</div>}
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-12">
                            <button type="submit" className="btn btn-primary">Save</button>
                            <button type="button" className="btn btn-secondary ms-2" onClick={handleCancel}>Cancel</button>
                        </div>
                    </div>
                </form>
            </div>
        </Layout>
    );
}

export default AddBlog;
